/* Inventory Snapshot — per-lot opening stock, movements over a period, and
 * closing stock.
 *
 *   GET  v1/inventory/snapshot?warehouseId=&from=&to=&search=&page=&size=
 *        -> { content: [{ lot, openingStrips, ..., looseUnitsReturned }], page }
 *   POST v1/reports/stock-snapshot/export  { warehouseId?, from, to, search? }
 *        -> task; polled + downloaded through reportApiShared's reportTasks
 *
 * The list needs exactly one warehouse; the export takes none for every store.
 * Rows come back sorted by warehouse, medicine name, batch — the backend
 * ignores any sort param, so none is sent.
 *
 * `lot` quantities are the CLOSING stock at the end of `to`. Prices and rack are
 * today's values, not the values on `to`. */
import { httpSupplier } from "../../HttpCommonALL";
import { unwrap, rethrow, normalizeTask, reportTasks, xUser, num } from "./reportApiShared";

const BASE = "v1/reports/stock-snapshot";

/* One API row -> a flat record the screen reads directly. Movement quantities
   are strips unless named loose*; outflows arrive as positive numbers, and only
   `adjustment` can be negative. */
const fromSnapshotRow = (r = {}) => {
  const l = r.lot || {};
  return {
    id: l.inventoryId,
    wh: l.warehouseId || "",
    warehouseName: l.warehouseName || "",
    medId: l.medicineCode || "",
    medicineName: l.medicineName || "",
    category: l.drugCategory || "",
    unitPack: l.unitPack || "",
    batch: l.batchNo || "",
    exp: l.expiryDate || "",
    purchase: num(l.purchasePrice),
    mrp: num(l.mrp),
    /* 0 means the pack size is unknown. */
    unitsPerStrip: num(l.unitsPerStrip),
    looseSaleSupported: !!l.looseSaleSupported,
    rackCode: l.rackCode || "",
    rackSource: l.rackSource || null,

    openingStrips: num(r.openingStrips),
    openingLoose: num(r.openingLooseUnits),
    closingStrips: num(l.quantityAvailable),
    closingLoose: num(l.looseUnitsAvailable),

    purchaseIn: num(r.purchaseIn),
    salesReturnIn: num(r.salesReturnIn),
    transferIn: num(r.transferIn),
    salesOut: num(r.salesOut),
    purchaseReturnOut: num(r.purchaseReturnOut),
    transferOut: num(r.transferOut),
    expiredDamaged: num(r.expiredDamaged),
    adjustment: num(r.adjustment),
    looseSold: num(r.looseUnitsSold),
    looseReturned: num(r.looseUnitsReturned),
  };
};

const stockSnapshotService = {
  /* GET v1/inventory/snapshot -> { content, totalPages, totalElements }.
     `from` / `to` are YYYY-MM-DD; opening is stock at 00:00 on `from`, closing
     is stock at the end of `to`. */
  list({ warehouseId, from, to, search, page = 0, size = 20 } = {}) {
    const params = { warehouseId, from, to, page, size };
    const term = (search || "").trim();
    if (term) params.search = term;
    return httpSupplier
      .get("v1/inventory/snapshot", { params, headers: xUser() })
      .then((r) => {
        const data = unwrap(r) || {};
        return {
          content: (data.content || []).map(fromSnapshotRow),
          totalPages: data.page?.totalPages ?? 0,
          totalElements: data.page?.totalElements ?? 0,
        };
      })
      .catch(rethrow);
  },

  /* POST v1/reports/stock-snapshot/export -> start CSV generation.
     `warehouseId` is dropped when blank: absent means every store (an empty
     string would read as a real filter). */
  requestExport({ warehouseId, from, to, search } = {}) {
    const body = { from, to };
    if (warehouseId) body.warehouseId = warehouseId;
    const term = (search || "").trim();
    if (term) body.search = term;
    return httpSupplier
      .post(`${BASE}/export`, body, { headers: xUser() })
      .then((r) => normalizeTask(unwrap(r)))
      .catch(rethrow);
  },

  exportStatus: (taskId) => reportTasks.status(taskId),

  download: (taskId, filename = "stock-snapshot.csv") => reportTasks.download(taskId, filename),
};

export default stockSnapshotService;
